import { ROLES } from '@constants/roles.ts';
import { USER_TYPES } from '@constants/userTypes.ts';
import { ROUTES } from '@router/constants.ts';
import type { UserInfoResponse } from '@routes/___shared___/user.api.ts';
import { START_PAGE_VALUES } from '@routes/sidebar/settings/ui/user-preferences-tab/constants.ts';
import { isEqualIgnoreCase } from '@utils/text.ts';
import type { LabeledValue } from 'antd/es/select';
import isNil from 'lodash/isNil';

// used in /settings
export const filterStartPageOptionsByUserAccess = (
    userInfo: UserInfoResponse | null,
    options: LabeledValue[],
    userRoles: string[],
): LabeledValue[] => {
    const canAccessAdmin = userRoles.includes(ROLES.ADMIN) || userRoles.includes(ROLES.BLUEBYTE_ADMIN);
    const canAccessFleetDashboard = userInfo?.userType === USER_TYPES.Office;
    const canAccessVesselDashboard =
        userInfo?.userType === USER_TYPES.Crew && !isNil(userInfo.employmentDetails?.crewEmploymentDetails?.vesselId);

    const accessMap = {
        [START_PAGE_VALUES.LEARNING]: true,
        [START_PAGE_VALUES.ADMIN]: canAccessAdmin,
        [START_PAGE_VALUES.FLEET_DASHBOARD]: canAccessFleetDashboard,
        [START_PAGE_VALUES.VESSEL_DASHBOARD]: canAccessVesselDashboard,
    };

    return options.filter((option) => accessMap[option.value as keyof typeof accessMap]);
};

// Tenant Admin is a Client Admin with fewer privileges. A higher admin role (blueByteAdmin, clientAdmin) wins.
// For now a Tenant Admin also has clientUser; a lower role like this does not change the result.
export const isTenantAdminOnly = (userRoles: string[]): boolean => {
    const hasRole = (role: string) => userRoles.some((userRole) => isEqualIgnoreCase(userRole, role));

    return hasRole(ROLES.TENANT_ADMIN) && !hasRole(ROLES.ADMIN) && !hasRole(ROLES.BLUEBYTE_ADMIN);
};

interface AdminLandingContext {
    userRoles: string[];
    clientId: number | null;
    tenantAlias: string | null;
}

interface AdminLandingRule {
    id: string;
    when: (context: AdminLandingContext) => boolean;
    to: (context: AdminLandingContext) => string;
}

const { ROOT, CONFIGURATION, VESSEL_DASHBOARD, FLEET_DASHBOARD, ASSESSOR } = ROUTES.SIDEBAR.ADMIN;

// The first matching rule wins. An exception for a role is a new rule above the general rules.
const ADMIN_LANDING_RULES: AdminLandingRule[] = [
    {
        id: 'tenant-admin',
        when: ({ userRoles, clientId, tenantAlias }) => isTenantAdminOnly(userRoles) && !!clientId && !!tenantAlias,
        to: ({ clientId, tenantAlias }) =>
            `${ROOT}/${CONFIGURATION.ROOT}/${clientId}/${CONFIGURATION.TENANT_INFO}/${tenantAlias}`,
    },
    {
        id: 'admin',
        when: ({ userRoles }) => userRoles.includes(ROLES.ADMIN) || userRoles.includes(ROLES.BLUEBYTE_ADMIN),
        to: () => ROOT,
    },
    {
        id: 'fleet-dashboard',
        when: ({ userRoles }) => userRoles.includes(ROLES.FLEET_DASHBOARD_VIEWER),
        to: () => `${ROOT}/${FLEET_DASHBOARD}`,
    },
    {
        id: 'vessel-dashboard',
        when: ({ userRoles }) => userRoles.includes(ROLES.VESSEL_DASHBOARD_VIEWER),
        to: () => `${ROOT}/${VESSEL_DASHBOARD}`,
    },
    {
        id: 'assessor',
        when: ({ userRoles }) => userRoles.includes(ROLES.CREW_ASSESSOR) || userRoles.includes(ROLES.OFFICE_ASSESSOR),
        to: () => `${ROOT}/${ASSESSOR.ROOT}`,
    },
];

// Used by the sidebar, the breadcrumb and the admin entry guard in the router.
export const getAdminLandingPath = (context: AdminLandingContext): string =>
    ADMIN_LANDING_RULES.find((rule) => rule.when(context))?.to(context) ?? ROOT;
